import { useEffect, useState } from 'react';
import { fetchTestFormulas, type PublicFormulaEntry } from '../services/testFormulas';

/**
 * useTestFormulas — fetches + caches the Formula Registry entries for one test
 * type and exposes a `formulaFor(columnKey)` lookup for grid headers
 * (FEAT "Formula Registry", 2026-07-15).
 *
 * Best-effort: if the fetch fails, `formulaFor` returns `undefined` for every
 * key, so `FormulaHeaderTooltip` degrades to its measured-value fallback (or the
 * column's own `note`) — the grid never breaks over a missing tooltip.
 *
 * Cached per test type at module scope so re-mounts (tab switches) don't refetch.
 */
type FormulaMap = Map<string, PublicFormulaEntry>;

const cache = new Map<string, FormulaMap>();

export interface UseTestFormulasResult {
  /** Registry entry for a column key, or undefined if unknown / not yet loaded. */
  formulaFor: (columnKey: string) => PublicFormulaEntry | undefined;
  ready: boolean;
}

export function useTestFormulas(testTypeId: string): UseTestFormulasResult {
  const [map, setMap] = useState<FormulaMap | undefined>(() => cache.get(testTypeId));

  useEffect(() => {
    let cancelled = false;
    const cached = cache.get(testTypeId);
    if (cached) {
      setMap(cached);
      return;
    }
    fetchTestFormulas(testTypeId)
      .then((entries) => {
        const m: FormulaMap = new Map(entries.map((e) => [e.columnKey, e]));
        cache.set(testTypeId, m);
        if (!cancelled) setMap(m);
      })
      .catch(() => {
        // Degrade gracefully — leave `map` undefined so tooltips fall back.
        if (!cancelled) setMap(undefined);
      });
    return () => { cancelled = true; };
  }, [testTypeId]);

  return {
    formulaFor: (columnKey: string) => map?.get(columnKey),
    ready: map !== undefined,
  };
}
